import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, History as HistoryIcon, CreditCard, ShieldCheck, LogOut, Coins, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { motion, AnimatePresence } from "framer-motion";

const NavItem = ({ to, icon: Icon, label, active, testid, onClick }) => (
  <Link
    to={to}
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
      active ? "bg-[#1F3A8A] text-white" : "text-[#64748B] hover:bg-[#F5F7FA] hover:text-[#111827]"
    }`}
  >
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    {label}
  </Link>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (p) => loc.pathname === p;
  const closeMobile = () => setMobileOpen(false);

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const sidebarContent = (
    <>
      <Link to="/dashboard" onClick={closeMobile} className="px-6 py-5 border-b border-[#E2E8F0] flex items-center justify-center">
        <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-16 w-auto object-contain" data-testid="sidebar-logo" />
      </Link>

      <nav className="flex-1 p-3 space-y-1">
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" active={isActive("/dashboard")} testid="nav-dashboard" onClick={closeMobile} />
        <NavItem to="/builder" icon={Sparkles} label="Prompt Builder" active={isActive("/builder")} testid="nav-builder" onClick={closeMobile} />
        <NavItem to="/history" icon={HistoryIcon} label="History" active={isActive("/history")} testid="nav-history" onClick={closeMobile} />
        <NavItem to="/plans" icon={CreditCard} label="Plans & Top-ups" active={isActive("/plans")} testid="nav-plans" onClick={closeMobile} />
        {user?.role === "admin" && (
          <NavItem to="/admin" icon={ShieldCheck} label="Admin" active={isActive("/admin")} testid="nav-admin" onClick={closeMobile} />
        )}
      </nav>

      <div className="p-3 border-t border-[#E2E8F0] space-y-2">
        <motion.div
          key={user?.tokens_remaining}
          initial={{ scale: 0.96 }}
          animate={{ scale: 1 }}
          className="px-3 py-2 rounded-md bg-[#F5F7FA] border border-[#E2E8F0]"
          data-testid="sidebar-token-balance"
        >
          <div className="flex items-center gap-2 text-xs text-[#64748B] uppercase tracking-wider font-semibold">
            <Coins className="w-3 h-3" /> Tokens
          </div>
          <div className="font-display text-2xl font-bold text-[#1F3A8A]">{user?.tokens_remaining ?? 0}</div>
        </motion.div>
        <div className="px-3 py-1">
          <div className="text-sm font-semibold text-[#111827] truncate">{user?.name}</div>
          <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#64748B] hover:text-[#EF4444] transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
        <div className="text-[10px] text-[#94A3B8] text-center pt-2 border-t border-[#F1F5F9] font-medium tracking-wider uppercase" data-testid="powered-by-gemini">
          Powered by Gemini Flash
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen lg:flex bg-[#F5F7FA]">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center">
          <img src="/promptyai-logo.png" alt="Promptyai.app" className="h-10 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="px-2.5 py-1 rounded bg-[#1F3A8A]/5 border border-[#1F3A8A]/20 text-xs font-semibold text-[#1F3A8A] flex items-center gap-1.5" data-testid="mobile-token-badge">
            <Coins className="w-3 h-3" /> {user?.tokens_remaining ?? 0}
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            className="p-2 text-[#111827]"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E2E8F0] flex-col" data-testid="app-sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-40 bg-black/40"
              onClick={closeMobile}
              data-testid="mobile-drawer-backdrop"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 bg-white border-r border-[#E2E8F0] flex flex-col"
              data-testid="mobile-sidebar"
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}
